import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, Loader2, Clock } from 'lucide-react';

/**
 * StepCard - Clean Header + Collapsible Body (no user action indicators)
 */
const StepCard = ({ 
  title, 
  status = 'pending', 
  defaultExpanded = true,
  autoCollapseOnComplete = true,
  summary = '',
  children,
  icon,
  duration = null,
  statusText = 'Processing',
  stepCount = 1
}) => {
  // Initialize expanded state based on status
  const [isExpanded, setIsExpanded] = useState(() => {
    return status === 'active' || defaultExpanded;
  });
  
  // Track previous status to detect changes
  const prevStatusRef = useRef(status);

  // Handle status changes with proper timing
  useEffect(() => {
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = status;
    
    if (prevStatus === status) return;
    
    if (autoCollapseOnComplete && status === 'completed') {
      // Collapse immediately when completed
      const timer = setTimeout(() => {
        setIsExpanded(false);
      }, 100);
      return () => clearTimeout(timer);
    }
    
    if (status === 'active') {
      // Expand immediately when becoming active
      setIsExpanded(true);
    }
  }, [status, autoCollapseOnComplete]);

  const getStatusBadge = () => {
    switch (status) {
      case 'pending':
        return (
          <span className="flex items-center gap-1.5 text-xs text-gray-400 bg-slate-700/50 px-2 py-0.5 rounded-full">
            Waiting
            <span className="inline-flex">
              <span className="animate-[bounce_1s_infinite_0ms]">.</span>
              <span className="animate-[bounce_1s_infinite_200ms]">.</span>
              <span className="animate-[bounce_1s_infinite_400ms]">.</span>
            </span>
          </span>
        );
      case 'active':
        return (
          <span className="flex items-center gap-1.5 text-xs text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">
            <Loader2 className="w-3 h-3 animate-spin" />
            {statusText}
          </span>
        );
      case 'completed':
        return (
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1 text-[10px] text-gray-500">
              <CheckCircle2 className="w-3 h-3 text-emerald-400" />
              {stepCount} {stepCount === 1 ? 'step' : 'steps'}
            </span>
            {duration && (
              <span className="flex items-center gap-1 text-[10px] text-gray-500 font-mono">
                <Clock className="w-3 h-3" />{duration}
              </span>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="rounded-xl bg-slate-950/50 overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full text-left p-4 flex items-center justify-between hover:bg-slate-900/30 transition-colors"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-gray-500">
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </span>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium text-gray-200">{title}</span>
            {!isExpanded && summary && (
              <p className="text-[10px] text-gray-500 mt-0.5 truncate">{summary}</p>
            )}
          </div>
        </div>
        {getStatusBadge()}
      </button>
      
      <div 
        className="grid transition-[grid-template-rows] duration-300 ease-in-out"
        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="px-4 pt-2 pb-4">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepCard;
